import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTheme } from "@/contexts/ThemeProvider";
import { Pencil, Redo2, Trash2, Undo2, X } from "lucide-react";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { ReactSketchCanvas } from "react-sketch-canvas";

// Color palette for the sketch tool
const COLORS = [
  { name: "White", value: "#FFFFFF" },
  { name: "Black", value: "#1a1a1a" },
  { name: "Tan", value: "#E8C872" },
  { name: "Coral", value: "#F28B82" },
  { name: "Teal", value: "#4ECDC4" },
  { name: "Blue", value: "#5B9BD5" },
];

export function SketchDialog({ open, onOpenChange, onSaveSketch }) {
  const { resolvedTheme } = useTheme();
  const canvasRef = useRef(null);
  const [currentColor, setCurrentColor] = useState("#FFFFFF");
  const [isSaving, setIsSaving] = useState(false);

  // Canvas background adapts to theme
  const canvasBgColor = resolvedTheme === "dark" ? "#262626" : "#f4f4f5";

  // Swap white/black for visibility on canvas background
  const getStrokeColor = (colorValue) => {
    if (resolvedTheme === "light") {
      if (colorValue === "#FFFFFF") return "#1a1a1a"; // White → Black
    } else {
      if (colorValue === "#1a1a1a") return "#FFFFFF"; // Black → White
    }
    return colorValue;
  };

  // Get display color for the color button (shows actual color in swatch)
  const getDisplayColor = (colorValue) => {
    // For the white/black swatches, show the theme-appropriate color
    if (resolvedTheme === "light" && colorValue === "#FFFFFF") {
      return "#1a1a1a";
    }
    if (resolvedTheme === "dark" && colorValue === "#1a1a1a") {
      return "#FFFFFF";
    }
    return colorValue;
  };

  const handleClose = () => {
    if (isSaving) return;
    onOpenChange(false);
  };

  const handleDone = async () => {
    if (!canvasRef.current) return;

    try {
      setIsSaving(true);
      const dataUrl = await canvasRef.current.exportImage("png");
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      const file = new File([blob], `sketch-${Date.now()}.png`, {
        type: "image/png",
      });

      const syntheticEvent = {
        target: { files: [file] },
        preventDefault: () => {},
        stopPropagation: () => {},
      };

      onSaveSketch(syntheticEvent);
      onOpenChange(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUndo = () => {
    canvasRef.current?.undo();
  };

  const handleRedo = () => {
    canvasRef.current?.redo();
  };

  const handleClear = () => {
    canvasRef.current?.clearCanvas();
  };

  const handleColorSelect = (color) => {
    setCurrentColor(color.value);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[90vw] max-h-[85vh] sm:max-h-[90vh] w-[800px] h-[600px] bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 text-gray-900 dark:text-white p-0 gap-0 rounded-2xl flex flex-col overflow-hidden shadow-2xl"
        hideCloseButton={true}
        onEscapeKeyDown={(e) => isSaving && e.preventDefault()}
        onPointerDownOutside={(e) => isSaving && e.preventDefault()}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          setTimeout(() => {
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
          }, 0);
        }}
      >
        <DialogTitle className="sr-only">Draw a sketch</DialogTitle>
        <DialogDescription className="sr-only">
          Draw a sketch to attach to your message
        </DialogDescription>

        {/* Close button */}
        <button
          onClick={handleClose}
          disabled={isSaving}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-zinc-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Canvas Area */}
        <div className="flex-1 p-4 pb-0">
          <div className="w-full h-full rounded-xl overflow-hidden">
            <ReactSketchCanvas
              ref={canvasRef}
              strokeColor={getStrokeColor(currentColor)}
              strokeWidth={4}
              canvasColor={canvasBgColor}
              style={{ border: "none" }}
              className="w-full h-full"
            />
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col items-center gap-2 p-4 pt-2">
          {/* Tools row */}
          <div className="flex items-center gap-1 px-3 py-2 bg-gray-100/90 dark:bg-zinc-700/90 backdrop-blur-md rounded-full border border-gray-200 dark:border-zinc-600">
            {/* Color buttons */}
            <div className="flex items-center gap-0.5 sm:gap-1">
              {COLORS.map((color) => {
                const isSelected = currentColor === color.value;
                return (
                  <button
                    key={color.name}
                    onClick={() => handleColorSelect(color)}
                    className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center transition-all hover:scale-110 ${
                      isSelected
                        ? "ring-2 ring-purple-400 ring-offset-2 ring-offset-gray-100 dark:ring-offset-zinc-700"
                        : ""
                    }`}
                    aria-label={color.name}
                    aria-pressed={isSelected}
                  >
                    <span
                      className="w-5 h-5 sm:w-6 sm:h-6 rounded-full border border-gray-300 dark:border-zinc-500"
                      style={{ backgroundColor: getDisplayColor(color.value) }}
                    />
                  </button>
                );
              })}
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-gray-300 dark:bg-zinc-500 mx-1 sm:mx-2" />

            {/* Tool buttons */}
            <div className="flex items-center gap-0.5 sm:gap-1">
              <button
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-gray-600 dark:text-zinc-300 hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors"
                aria-label="Pencil tool"
              >
                <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              <button
                onClick={handleUndo}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-gray-600 dark:text-zinc-300 hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors"
                aria-label="Undo"
              >
                <Undo2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              <button
                onClick={handleRedo}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-gray-600 dark:text-zinc-300 hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors"
                aria-label="Redo"
              >
                <Redo2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>

              <button
                onClick={handleClear}
                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-gray-600 dark:text-zinc-300 hover:bg-gray-200 dark:hover:bg-zinc-600 transition-colors"
                aria-label="Clear canvas"
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>

          {/* Done button - separate row for better mobile visibility */}
          <Button
            onClick={handleDone}
            disabled={isSaving}
            className="bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 px-6 sm:px-4 rounded-full text-sm font-semibold h-10 sm:h-9 shadow-sm transition-all min-w-[120px]"
          >
            {isSaving ? "Saving..." : "Done"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

SketchDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  onSaveSketch: PropTypes.func.isRequired,
};

export default SketchDialog;
